import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, Filter, X, ChevronDown, Loader2 } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import ProductCard from '../components/products/ProductCard';
import { productsAPI, categoriesAPI } from '../services/api';

const ProductsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('default');
  const [showFilters, setShowFilters] = useState(false);

  const selectedCategory = searchParams.get('category') || '';

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsRes, categoriesRes] = await Promise.all([
          productsAPI.getAll({ category: selectedCategory || undefined }),
          categoriesAPI.getAll()
        ]);
        setProducts(productsRes.data);
        setCategories(categoriesRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [selectedCategory]);

  const filteredProducts = useMemo(() => {
    let result = [...products];

    // Filter by search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        p =>
          p.name.toLowerCase().includes(query) ||
          p.description.toLowerCase().includes(query) ||
          p.category.toLowerCase().includes(query)
      );
    }

    // Sort
    switch (sortBy) {
      case 'price-low':
        result.sort((a, b) => a.price - b.price);
        break;
      case 'price-high':
        result.sort((a, b) => b.price - a.price);
        break;
      case 'name':
        result.sort((a, b) => a.name.localeCompare(b.name));
        break;
      default:
        break;
    }

    return result;
  }, [products, searchQuery, sortBy]);

  const handleCategoryChange = (category) => {
    if (category === selectedCategory) {
      searchParams.delete('category');
    } else {
      searchParams.set('category', category);
    }
    setSearchParams(searchParams);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSortBy('default');
    setSearchParams({});
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-amber-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">All Products</h1>
          <p className="text-zinc-400">
            {filteredProducts.length} products available
          </p>
        </div>

        {/* Filters Bar */}
        <div className="bg-zinc-900 rounded-xl p-4 mb-8 border border-zinc-800">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
              <Input
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
              />
            </div>

            {/* Sort */}
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-full lg:w-48 bg-zinc-800 border-zinc-700 text-white">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-800 border-zinc-700">
                <SelectItem value="default" className="text-white">Default</SelectItem>
                <SelectItem value="price-low" className="text-white">Price: Low to High</SelectItem>
                <SelectItem value="price-high" className="text-white">Price: High to Low</SelectItem>
                <SelectItem value="name" className="text-white">Name: A-Z</SelectItem>
              </SelectContent>
            </Select>

            {/* Mobile filter toggle */}
            <Button
              variant="outline"
              className="lg:hidden border-zinc-700 text-white"
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter className="h-4 w-4 mr-2" />
              Categories
              <ChevronDown className={`h-4 w-4 ml-2 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
            </Button>
          </div>

          {/* Category filters - Desktop */}
          <div className="hidden lg:flex flex-wrap gap-2 mt-4">
            {categories.map((category) => (
              <Button
                key={category.id}
                variant={selectedCategory === category.slug ? 'default' : 'outline'}
                size="sm"
                onClick={() => handleCategoryChange(category.slug)}
                className={`${
                  selectedCategory === category.slug
                    ? 'bg-amber-500 text-black hover:bg-amber-600'
                    : 'border-zinc-700 text-zinc-300 hover:text-white hover:bg-zinc-800'
                }`}
              >
                {category.name}
              </Button>
            ))}
            
            {(selectedCategory || searchQuery || sortBy !== 'default') && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="text-zinc-400 hover:text-white"
              >
                <X className="h-4 w-4 mr-1" />
                Clear filters
              </Button>
            )}
          </div>

          {/* Category filters - Mobile */}
          {showFilters && (
            <div className="lg:hidden flex flex-wrap gap-2 mt-4 pt-4 border-t border-zinc-800">
              {categories.map((category) => (
                <Button
                  key={category.id}
                  variant={selectedCategory === category.slug ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handleCategoryChange(category.slug)}
                  className={`${
                    selectedCategory === category.slug
                      ? 'bg-amber-500 text-black hover:bg-amber-600'
                      : 'border-zinc-700 text-zinc-300 hover:text-white hover:bg-zinc-800'
                  }`}
                >
                  {category.name}
                </Button>
              ))}
            </div>
          )}
        </div>

        {/* Active filters */}
        {selectedCategory && (
          <div className="flex items-center gap-2 mb-6">
            <span className="text-zinc-400 text-sm">Filtering by:</span>
            <Badge 
              variant="outline" 
              className="border-amber-500/50 text-amber-500 cursor-pointer hover:bg-amber-500/10"
              onClick={() => handleCategoryChange(selectedCategory)}
            >
              {categories.find(c => c.slug === selectedCategory)?.name}
              <X className="h-3 w-3 ml-1" />
            </Badge>
          </div>
        )}

        {/* Products Grid */}
        {filteredProducts.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredProducts.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="w-20 h-20 bg-zinc-800 rounded-full flex items-center justify-center mx-auto mb-4">
              <Search className="h-10 w-10 text-zinc-600" />
            </div>
            <h3 className="text-white text-xl font-semibold mb-2">No products found</h3>
            <p className="text-zinc-400 mb-4">Try adjusting your search or filters</p>
            <Button
              onClick={clearFilters}
              className="bg-amber-500 hover:bg-amber-600 text-black"
            >
              Clear all filters
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductsPage;
